/* eslint-disable react-hooks/exhaustive-deps */

/* eslint-disable @typescript-eslint/no-explicit-any */
import React from 'react';
import { useTranslation } from 'react-i18next';

import dayjs, { Dayjs } from 'dayjs';
import 'dayjs/locale/be';
import 'dayjs/locale/en';
import 'dayjs/locale/kk';
import 'dayjs/locale/ky';
import 'dayjs/locale/ru';
import 'dayjs/locale/uz-latn';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Tooltip, Typography, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { beBY, enUS, kzKZ, ruRU } from '@mui/x-date-pickers/locales';

import { TypeEventSelect } from '@entities/type_event_select';
import { openNativeDatePickerFromHiddenInput } from '@shared/lib/openNativeDatePickerFromHiddenInput';
import { StyledTable } from '@shared/styled_components/styledTable';
import { HiddenFiltersOfDates } from '@shared/ui/hidden_filters_of_dates';
import { InputDate } from '@shared/ui/input_date/InputDate';
import { ResetFilters } from '@shared/ui/reset_filters/ResetFilters';
import { Values } from '@shared/ui/search_multiple_select';
import { FilterButton } from '@shared/ui/table_filter_button/FilterButton';
import { TouchLoader } from '@shared/ui/touch_loader';

import i18n from '../../../i18n';
import style from '../ui/EventsHistory.module.scss';

interface EventsFilterPanelProps {
  open: boolean;
  onFilterChange: (values?: Values) => void;
  sortField: 'id' | 'timestamp' | null;
  sortOrder: 'ASC' | 'DESC' | null;
  onRequestSort: (property: 'id' | 'timestamp') => void;
  disableFilters?: boolean;
  // Новые пропсы для сохранения состояния фильтров
  initialTypeEventFilters?: Values;
  initialStartDate?: Dayjs | null;
  initialEndDate?: Dayjs | null;
  /** См. EventsHistory `sidePanelMobileFilterUx`. */
  sidePanelMobileFilterUx?: boolean;
  /** См. EventsHistory `sidePanelFilterUxAlways`. */
  sidePanelFilterUxAlways?: boolean;
}

export const Text = (text: string) => (
  <Typography marginTop={1} width={'100%'} textAlign={'center'} fontSize={20} fontWeight={500}>
    {text}
  </Typography>
);

export const getTextList = (isLoading: boolean, length: number) => {
  if (isLoading) return <TouchLoader />;
  if (!isLoading && length > 0) return Text(i18n.t('history.noMoreEvents'));
  if (!isLoading && length === 0) return Text(i18n.t('history.noEvents'));
};

export const TableHeader = ({
  onFilterChange,
  setStartDate,
  setEndDate,
  startDate,
  endDate,
  sortField,
  sortOrder,
  onRequestSort,
  disableFilters = false,
  // Новые пропсы для сохранения состояния фильтров
  initialTypeEventFilters = [],
  initialStartDate = null,
  initialEndDate = null,
  sidePanelMobileFilterUx = false,
  sidePanelFilterUxAlways = false,
}: EventsFilterPanelProps & {
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
}) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  /** Синхронно с `EventsHistory.module.scss` (@media max-width: 768px). */
  const isMobileHistoryFilters = useMediaQuery('(max-width:768px)');
  const useSidePanelMobileUx =
    (isMobileHistoryFilters && sidePanelMobileFilterUx) || sidePanelFilterUxAlways;
  const lang = (i18n.language || 'ru').split('-')[0].toLowerCase();

  const pickersLocalePack: Record<string, typeof ruRU | typeof enUS | typeof kzKZ | typeof beBY> = {
    ru: ruRU,
    en: enUS,
    kk: kzKZ,
    ky: ruRU,
    be: beBY,
    uz: enUS,
  };
  const dayjsLocaleByLang: Record<string, string> = {
    ru: 'ru',
    en: 'en',
    kk: 'kk',
    ky: 'ky',
    be: 'be',
    uz: 'uz-latn',
  };
  const pickerPack = pickersLocalePack[lang] || ruRU;
  const localeText = pickerPack.components.MuiLocalizationProvider.defaultProps.localeText;
  const adapterLocale = dayjsLocaleByLang[lang] || 'ru';

  React.useEffect(() => {
    dayjs.locale(adapterLocale);
  }, [adapterLocale]);

  const [localStartDate, setLocalStartDate] = React.useState<Dayjs | null>(initialStartDate);
  const [localEndDate, setLocalEndDate] = React.useState<Dayjs | null>(initialEndDate);
  const [typeEventFilters, setTypeEventFilters] = React.useState<Values>(initialTypeEventFilters);
  const [showFilters, setShowFilters] = React.useState(false);
  const [hoveredColumn, setHoveredColumn] = React.useState<'id' | 'timestamp' | null>(null);
  const [startDateInput, setStartDateInput] = React.useState('');
  const [endDateInput, setEndDateInput] = React.useState('');
  const [startDateError, setStartDateError] = React.useState('');
  const [endDateError, setEndDateError] = React.useState('');
  const startDateNativeRef = React.useRef<HTMLInputElement>(null);
  const endDateNativeRef = React.useRef<HTMLInputElement>(null);
  // Добавляем ключ для принудительного сброса TypeEventSelect
  const [typeEventSelectKey, setTypeEventSelectKey] = React.useState(0);

  // Инициализируем фильтры начальными значениями при монтировании
  React.useEffect(() => {
    setLocalStartDate(initialStartDate);
    setLocalEndDate(initialEndDate);
    setTypeEventFilters(initialTypeEventFilters);
    onFilterChange(initialTypeEventFilters);
    setStartDate(initialStartDate);
    setEndDate(initialEndDate);
  }, []);

  React.useEffect(() => {
    setLocalStartDate(startDate);
    setLocalEndDate(endDate);
  }, [startDate, endDate]);

  React.useEffect(() => {
    setStartDateInput(localStartDate ? localStartDate.format('DD.MM.YYYY') : '');
    setStartDateError('');
  }, [localStartDate]);

  React.useEffect(() => {
    setEndDateInput(localEndDate ? localEndDate.format('DD.MM.YYYY') : '');
    setEndDateError('');
  }, [localEndDate]);

  const applyDateMask = (value: string): string => {
    const numbers = value.replace(/\D/g, '');
    let result = '';
    for (let i = 0; i < numbers.length; i++) {
      if (i === 2 || i === 4) result += '.';
      if (i >= 8) break;
      result += numbers[i];
    }
    return result;
  };

  const parseDateFromInput = (inputValue: string): Dayjs | null => {
    if (!/^\d{2}\.\d{2}\.\d{4}$/.test(inputValue)) return null;
    const [dayStr, monthStr, yearStr] = inputValue.split('.');
    const day = Number(dayStr);
    const month = Number(monthStr);
    const year = Number(yearStr);
    if (year < 1900 || year > 2100 || month < 1 || month > 12 || day < 1 || day > 31) return null;
    const daysInMonth = new Date(year, month, 0).getDate();
    if (day > daysInMonth) return null;
    const d = dayjs(`${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`);
    return d.isValid() ? d : null;
  };

  const handleStartDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setStartDateInput(masked);
    if (masked.length === 10) {
      const parsed = parseDateFromInput(masked);
      if (parsed) {
        setStartDateError('');
        handleStartDateChange(parsed);
      } else {
        setStartDateError(t('validation.invalidDate'));
      }
    } else if (masked.length === 0) {
      setStartDateError('');
      handleStartDateChange(null);
    } else {
      setStartDateError('');
    }
  };

  const handleEndDateInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const masked = applyDateMask(e.target.value);
    setEndDateInput(masked);
    if (masked.length === 10) {
      const parsed = parseDateFromInput(masked);
      if (parsed) {
        setEndDateError('');
        handleEndDateChange(parsed);
      } else {
        setEndDateError(t('validation.invalidDate'));
      }
    } else if (masked.length === 0) {
      setEndDateError('');
      handleEndDateChange(null);
    } else {
      setEndDateError('');
    }
  };

  const handleStartDateInputBlur = () => {
    if (startDateInput && startDateInput.length < 10) {
      setStartDateInput(localStartDate ? localStartDate.format('DD.MM.YYYY') : '');
      setStartDateError('');
    }
  };

  const handleEndDateInputBlur = () => {
    if (endDateInput && endDateInput.length < 10) {
      setEndDateInput(localEndDate ? localEndDate.format('DD.MM.YYYY') : '');
      setEndDateError('');
    }
  };

  const handleOpenStartCalendar = () => {
    const input = startDateNativeRef.current;
    if (input) input.value = localStartDate ? localStartDate.format('YYYY-MM-DD') : '';
    openNativeDatePickerFromHiddenInput(input);
  };

  const handleOpenEndCalendar = () => {
    const input = endDateNativeRef.current;
    if (input) input.value = localEndDate ? localEndDate.format('YYYY-MM-DD') : '';
    openNativeDatePickerFromHiddenInput(input);
  };

  const handleStartNativeCommit = (value: string) => {
    if (!value) {
      handleStartDateChange(null);
      setStartDateInput('');
      setStartDateError('');
      return;
    }
    const parsed = dayjs(value);
    if (parsed.isValid()) {
      handleStartDateChange(parsed);
      setStartDateInput(parsed.format('DD.MM.YYYY'));
      setStartDateError('');
    }
  };

  const handleEndNativeCommit = (value: string) => {
    if (!value) {
      handleEndDateChange(null);
      setEndDateInput('');
      setEndDateError('');
      return;
    }
    const parsed = dayjs(value);
    if (parsed.isValid()) {
      handleEndDateChange(parsed);
      setEndDateInput(parsed.format('DD.MM.YYYY'));
      setEndDateError('');
    }
  };

  const handleStartDateChange = (newValue: Dayjs | null) => {
    setLocalStartDate(newValue);
    if (newValue?.isValid()) setStartDate(newValue);
    else setStartDate(null);
  };

  const handleEndDateChange = (newValue: Dayjs | null) => {
    setLocalEndDate(newValue);
    if (newValue?.isValid()) setEndDate(newValue);
    else setEndDate(null);
  };

  const handleEventFilterChange = (value: Values) => {
    // Фильтруем только валидные числовые значения для API
    const validValues = value.filter((v) => !isNaN(Number(v.value)));
    setTypeEventFilters(validValues);
    onFilterChange(validValues);
  };

  const toggleFilters = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowFilters(!showFilters);
  };

  const handleSortClick = (field: 'id' | 'timestamp') => {
    onRequestSort(field);
  };

  const resetAllFilters = () => {
    // Сбрасываем все состояния полностью
    setStartDate(null);
    setEndDate(null);
    setTypeEventFilters([]);
    onFilterChange([]);
    setLocalStartDate(null);
    setLocalEndDate(null);
    // Принудительно сбрасываем TypeEventSelect через изменение ключа
    setTypeEventSelectKey((prev) => prev + 1);
  };

  return (
    <LocalizationProvider
      dateAdapter={AdapterDayjs}
      localeText={localeText}
      adapterLocale={adapterLocale}>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <StyledTable.HeaderRow className={style.headerTitleRow}>
            <StyledTable.HeaderCell
              className={style.typeOfEventTitle}
              onClick={() => handleSortClick('id')}
              onMouseEnter={() => setHoveredColumn('id')}
              onMouseLeave={() => setHoveredColumn(null)}
              sx={{ cursor: 'pointer', position: 'relative' }}>
              {t('tables.eventType')}
              <span style={{ display: 'inline-block', position: 'absolute', marginLeft: 10 }}>
                <Tooltip title={t('common.sortTooltip')}>
                  <span style={{ display: 'inline-flex' }}>
                    {(sortField === 'id' || hoveredColumn === 'id') && (
                      <ArrowUpwardIcon
                        sx={{
                          fontSize: '1rem',
                          color:
                            sortField === 'id'
                              ? theme.palette.text.primary
                              : theme.palette.text.secondary,
                          transform:
                            sortField === 'id' && sortOrder === 'DESC'
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                          opacity: sortField === 'id' ? 1 : 0.5,
                        }}
                      />
                    )}
                  </span>
                </Tooltip>
              </span>
            </StyledTable.HeaderCell>

            <StyledTable.HeaderCell className={style.dateTitle}>
              <div className={style.dateTitleContent} style={{ position: 'relative' }}>
                <div
                  onClick={() => handleSortClick('timestamp')}
                  onMouseEnter={() => setHoveredColumn('timestamp')}
                  onMouseLeave={() => setHoveredColumn(null)}
                  style={{
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                    paddingRight: 30,
                  }}>
                  <span>{t('tables.date')}</span>
                  <span
                    style={{
                      display: 'inline-block',
                      position: 'absolute',
                      left: 'calc(100% - 20px)',
                    }}>
                    <Tooltip title={t('common.sortTooltip')}>
                      <span style={{ display: 'inline-flex' }}>
                        {(sortField === 'timestamp' || hoveredColumn === 'timestamp') && (
                          <ArrowUpwardIcon
                            sx={{
                              fontSize: '1rem',
                              color:
                                sortField === 'timestamp'
                                  ? theme.palette.text.primary
                                  : theme.palette.text.secondary,
                              transform:
                                sortField === 'timestamp' && sortOrder === 'DESC'
                                  ? 'rotate(180deg)'
                                  : 'rotate(0deg)',
                              opacity: sortField === 'timestamp' ? 1 : 0.5,
                            }}
                          />
                        )}
                      </span>
                    </Tooltip>
                  </span>
                </div>
              </div>
            </StyledTable.HeaderCell>

            <StyledTable.HeaderCell className={style.headerCell}>
              <div className={style.headerFilterCellInner}>
                <FilterButton
                  open={showFilters}
                  //@ts-expect-error: "Временное решение"
                  toggle={toggleFilters}
                  active={showFilters}
                  testid="filter-button"
                  disabled={disableFilters}
                  iconOnly={useSidePanelMobileUx}
                />
              </div>
            </StyledTable.HeaderCell>
          </StyledTable.HeaderRow>

          {showFilters && !disableFilters && (
            <StyledTable.HeaderRow className={`${style.tr} ${style.filterRow}`}>
              <StyledTable.HeaderCell
                className={style.typeOfEvent}
                style={{ paddingRight: '5px', width: '160px' }}>
                {/* Добавляем key для принудительного сброса */}
                <TypeEventSelect
                  key={`type-event-select-${typeEventSelectKey}`} // Ключ для принудительного сброса
                  multiple={true}
                  name="typeEvent"
                  setValueStore={(_, value) => handleEventFilterChange(value)}
                  value={typeEventFilters}
                  label={t('filters.eventType')}
                  getTooltipTitle={(label) => `${label}`}
                  mobileModalPicker={useSidePanelMobileUx}
                  sx={{ width: 150 }}
                />
              </StyledTable.HeaderCell>

              <StyledTable.HeaderCell
                className={style.headerCellDate}
                style={{ paddingLeft: '5px', position: 'relative', width: 'auto' }}>
                <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: '5px' }}>
                  <div
                    className={style.datePickerContainer}
                    style={{ display: 'flex', gap: '50px', flexShrink: 0 }}>
                    <InputDate
                      value={localStartDate}
                      onChange={handleStartDateChange}
                      tooltipTitle={t('history.startDate')}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '56px',
                          width: '200px',
                        },
                      }}
                    />
                    <InputDate
                      value={localEndDate}
                      onChange={handleEndDateChange}
                      tooltipTitle={t('history.endDate')}
                      sx={{
                        '& .MuiInputBase-root': {
                          height: '56px',
                          width: '200px',
                        },
                      }}
                    />
                  </div>
                  {/* Десктопная версия - кнопка справа от полей дат */}
                  <div style={{ marginLeft: '50px', marginRight: '20px' }}>
                    <ResetFilters reset={resetAllFilters} />
                  </div>
                </div>
              </StyledTable.HeaderCell>

              <StyledTable.HeaderCell className={style.headerCell} />
            </StyledTable.HeaderRow>
          )}

          {/* Мобильная версия - дополнительный ряд с кнопкой справа от "Тип события" */}
          {showFilters && !disableFilters && (
            <StyledTable.HeaderRow
              className={`${style.tr} ${style.filterRowMobile}`}
              style={{ display: 'none' }}>
              <StyledTable.HeaderCell
                className={style.typeOfEvent}
                style={{ paddingRight: '5px', width: '100%' }}>
                <div className={style.mobileFilterTypeBlock}>
                  <div className={style.mobileFilterTypeRow}>
                    <div className={style.mobileFilterTypeGrow}>
                      <TypeEventSelect
                        key={`type-event-select-mobile-${typeEventSelectKey}`}
                        multiple={true}
                        name="typeEvent"
                        setValueStore={(_, value) => handleEventFilterChange(value)}
                        value={typeEventFilters}
                        label={t('filters.eventType')}
                        getTooltipTitle={(label) => `${label}`}
                        mobileModalPicker={useSidePanelMobileUx}
                        sx={{ width: '100%' }}
                      />
                      {useSidePanelMobileUx && (
                        <span className={style.mobileTypeHintIcon} aria-hidden="true">
                          <KeyboardArrowDown fontSize="small" />
                        </span>
                      )}
                    </div>
                    {useSidePanelMobileUx && (
                      <div className={style.mobileFilterResetSlot}>
                        <ResetFilters reset={resetAllFilters} />
                      </div>
                    )}
                  </div>
                </div>
              </StyledTable.HeaderCell>

              <StyledTable.HeaderCell
                className={style.headerCellDate}
                style={{ paddingLeft: '5px', position: 'relative', width: '100%' }}>
                <div className={style.mobileFilterDateBlock}>
                  <HiddenFiltersOfDates
                    isOpen={true}
                    forceExpanded={true}
                    hideToggleRow={true}
                    hideResetButton={true}
                    onToggle={() => undefined}
                    onReset={resetAllFilters}
                    startPlaceholder={t('datePlaceholder')}
                    endPlaceholder={t('datePlaceholder')}
                    startValue={startDateInput}
                    endValue={endDateInput}
                    startError={startDateError}
                    endError={endDateError}
                    onStartChange={handleStartDateInputChange}
                    onEndChange={handleEndDateInputChange}
                    onStartBlur={handleStartDateInputBlur}
                    onEndBlur={handleEndDateInputBlur}
                    onOpenStartCalendar={handleOpenStartCalendar}
                    onOpenEndCalendar={handleOpenEndCalendar}
                    onClearStart={() => {
                      setStartDateInput('');
                      setStartDateError('');
                      handleStartDateChange(null);
                    }}
                    onClearEnd={() => {
                      setEndDateInput('');
                      setEndDateError('');
                      handleEndDateChange(null);
                    }}
                    startDateInputRef={startDateNativeRef}
                    endDateInputRef={endDateNativeRef}
                    startDateIso={localStartDate ? localStartDate.format('YYYY-MM-DD') : ''}
                    endDateIso={localEndDate ? localEndDate.format('YYYY-MM-DD') : ''}
                    onStartNativeCommit={handleStartNativeCommit}
                    onEndNativeCommit={handleEndNativeCommit}
                  />
                </div>
              </StyledTable.HeaderCell>

              <StyledTable.HeaderCell className={style.headerCell} />
            </StyledTable.HeaderRow>
          )}
        </thead>
      </table>
    </LocalizationProvider>
  );
};
